/* ============================================================
   MODULO — POPUP WELCOME
   One controller per [data-modulo-pw] section. Triggers are armed
   immediately (the dialog is fixed-position, not in-view lazy).
   Center = modal (scroll-lock + focus-trap); corners = non-modal
   slide-in that leaves the page interactive.
   Shares its controller shape with Popup Studio (.modulo-pst) and
   the Signup Popup (.modulo-psu) — fix a bug here in all three.
   ============================================================ */
(function () {
  "use strict";
  if (typeof window === "undefined" || typeof document === "undefined") return;

  var REDUCED = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var DESIGN = !!(window.Shopify && window.Shopify.designMode);
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* ---- body scroll-lock (modal only), instance-counted, gutter-compensated ---- */
  var lockCount = 0, savedY = 0;
  function lockScroll() {
    if (lockCount++ === 0) {
      savedY = window.scrollY || window.pageYOffset || 0;
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.body.style.paddingRight = sbw + "px";
      document.body.style.top = -savedY + "px";
      document.body.classList.add("modulo-pw--locked");
    }
  }
  function unlockScroll() {
    if (lockCount > 0 && --lockCount === 0) {
      document.body.classList.remove("modulo-pw--locked");
      document.body.style.top = "";
      document.body.style.paddingRight = "";
      window.scrollTo(0, savedY);
    }
  }

  /* ============================================================
     FORM — validate + AJAX submit + success + optional copy
     ============================================================ */
  function makeForm(root) {
    var form = root.querySelector(".modulo-pw__form");
    if (!form) return null;
    var email = root.querySelector("[data-pw-email]");
    var errEl = root.querySelector("[data-pw-err]");
    var doneEl = root.querySelector("[data-pw-done]");
    var claim = root.querySelector("[data-pw-claim]");
    var closeBtn = root.querySelector("[data-pw-close]");
    var honeypot = root.querySelector("[data-pw-hp]");
    var copyBtn = root.querySelector("[data-pw-copy]");
    var copyLbl = root.querySelector("[data-pw-copylabel]");
    var copyText = copyLbl ? copyLbl.textContent : "Copy";

    function showError() {
      if (errEl) errEl.classList.add("show");
      if (email) email.setAttribute("aria-invalid", "true");
    }
    function clearError() {
      if (email) email.removeAttribute("aria-invalid");
      if (errEl) errEl.classList.remove("show");
    }
    function settle() {
      form.classList.add("is-done");
      if (doneEl) doneEl.classList.add("show");
      if (claim) { claim.removeAttribute("aria-busy"); claim.disabled = true; }
      var target = copyBtn || closeBtn;
      if (target) setTimeout(function () { try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); } }, 140);
    }

    form.addEventListener("submit", function (e) {
      if (form.dataset.pwDone === "1") { e.preventDefault(); return; }
      if (honeypot && honeypot.value) { e.preventDefault(); return; }
      var v = (email && email.value || "").trim();
      if (!v || !EMAIL_RE.test(v)) { e.preventDefault(); showError(); if (email) email.focus(); return; }
      clearError();
      // no fetch/FormData → let the native POST + reload reveal the success state
      if (!window.fetch || !window.FormData) { form.dataset.pwDone = "1"; return; }
      e.preventDefault();
      form.dataset.pwDone = "1";
      if (claim) claim.setAttribute("aria-busy", "true");
      var action = form.getAttribute("action") || window.location.pathname;
      var done = false;
      var finish = function () { if (done) return; done = true; if (REDUCED) settle(); else setTimeout(settle, 300); };
      // a rejected server response (4xx/5xx) must NOT render a false "you're in" —
      // fall back to the native POST so the real Shopify result / error is shown.
      var native = function () { form.dataset.pwDone = ""; form.submit(); };
      fetch(action, { method: "POST", body: new FormData(form), headers: { "Accept": "text/html" }, credentials: "same-origin" })
        .then(function (r) { if (r && r.ok === false) { native(); return; } finish(); }, native);
    });

    if (email) {
      email.addEventListener("input", function () {
        if (email.getAttribute("aria-invalid") === "true" && EMAIL_RE.test(email.value.trim())) clearError();
      });
    }

    if (copyBtn) {
      copyBtn.addEventListener("click", function () {
        var code = copyBtn.getAttribute("data-code") || "";
        var notify = function () {
          copyBtn.classList.add("is-copied");
          if (copyLbl) copyLbl.textContent = "Copied";
          setTimeout(function () { copyBtn.classList.remove("is-copied"); if (copyLbl) copyLbl.textContent = copyText; }, 1900);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(notify, fallback);
        else fallback();
        function fallback() {
          var t = document.createElement("textarea");
          t.value = code; t.style.position = "fixed"; t.style.opacity = "0";
          document.body.appendChild(t); t.select();
          try { document.execCommand("copy"); } catch (_) { }
          t.remove(); notify();
        }
      });
    }

    return {
      markDone: function () {
        form.dataset.pwDone = "1"; form.classList.add("is-done");
        if (doneEl) doneEl.classList.add("show");
        if (claim) claim.disabled = true;
      }
    };
  }

  /* ============================================================
     POPUP CONTROLLER — open/close, focus trap (modal), ESC
     ============================================================ */
  function wirePopup(sectionEl) {
    var scrim = sectionEl.querySelector("[data-pw-scrim]");
    if (!scrim) return null;
    var dialog = scrim.querySelector("[data-pw-dialog]");
    var closeEls = scrim.querySelectorAll("[data-pw-close]");
    var launcher = sectionEl.querySelector("[data-pw-launcher]");
    var MODAL = sectionEl.getAttribute("data-modal") !== "false";
    var isOpen = false, lastFocus = null;

    var titleEl = scrim.querySelector("[data-pw-title]");
    var subEl = scrim.querySelector("[data-pw-sub]");
    var uid = "pw-" + (sectionEl.getAttribute("data-section-id") || Math.random().toString(36).slice(2, 8));
    if (titleEl && dialog) { titleEl.id = uid + "-t"; dialog.setAttribute("aria-labelledby", uid + "-t"); }
    if (subEl && dialog) { subEl.id = uid + "-s"; dialog.setAttribute("aria-describedby", uid + "-s"); }

    var formCtl = makeForm(scrim);

    function focusables() {
      return Array.prototype.slice.call(dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function (el) { return el.offsetParent !== null; });
    }

    function open(opts) {
      if (isOpen) return;
      isOpen = true;
      lastFocus = document.activeElement;
      scrim.hidden = false;
      scrim.classList.remove("is-closing");
      if (MODAL) lockScroll();
      if (launcher) launcher.classList.remove("show");
      dialog.style.animation = "none"; void dialog.offsetWidth; dialog.style.animation = "";
      // Move focus into the dialog only when it's modal OR the shopper opened it
      // themselves (launcher click). A non-modal auto-open must NOT steal focus.
      if (MODAL || (opts && opts.userInitiated)) {
        requestAnimationFrame(function () {
          // focus the close (NOT the email) so mobile keyboards never shove the sheet
          var first = scrim.querySelector("[data-pw-close]");
          if (first) try { first.focus({ preventScroll: true }); } catch (_) { first.focus(); }
        });
      }
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      if (MODAL) unlockScroll();
      var done = function () {
        scrim.hidden = true;
        scrim.classList.remove("is-closing");
        if (launcher) launcher.classList.add("show");
        if (lastFocus && lastFocus.focus) try { lastFocus.focus({ preventScroll: true }); } catch (_) { }
      };
      if (REDUCED) { done(); return; }
      scrim.classList.add("is-closing");
      var fired = false;
      var fin = function () { if (fired) return; fired = true; scrim.removeEventListener("transitionend", fin); done(); };
      scrim.addEventListener("transitionend", fin);
      setTimeout(fin, 460);
    }

    // immediate release (no animation) — used on theme-editor section teardown
    function release() { if (isOpen) { isOpen = false; if (MODAL) unlockScroll(); } }

    for (var i = 0; i < closeEls.length; i++) closeEls[i].addEventListener("click", close);

    // scrim click closes — modal only (non-modal scrim is pointer-events:none)
    if (MODAL) {
      var downOnScrim = false;
      scrim.addEventListener("mousedown", function (e) { downOnScrim = e.target === scrim; });
      scrim.addEventListener("mouseup", function (e) { if (downOnScrim && e.target === scrim) close(); downOnScrim = false; });
    }

    scrim.addEventListener("keydown", function (e) {
      if (scrim.hidden) return;
      if (e.key === "Escape") { e.preventDefault(); close(); return; }
      if (MODAL && e.key === "Tab") {
        var f = focusables(); if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    // non-modal: focus isn't trapped inside, so ESC must work from anywhere on the page
    if (!MODAL) {
      document.addEventListener("keydown", function (e) {
        if (isOpen && e.key === "Escape") { e.preventDefault(); close(); }
      });
    }

    if (launcher) launcher.addEventListener("click", function () { open({ userInitiated: true }); });

    return {
      open: open, close: close, release: release, form: formCtl,
      showLauncher: function () { if (launcher) launcher.classList.add("show"); }
    };
  }

  /* ============================================================
     TRIGGERS + FREQUENCY CAP
     ============================================================ */
  function storeKey(el) { return "modulo-pw-" + (el.getAttribute("data-section-id") || "x"); }
  function suppressed(el) {
    var days = parseInt(el.getAttribute("data-frequency"), 10) || 0;
    if (days <= 0) return false;
    try {
      var ts = parseInt(window.localStorage.getItem(storeKey(el)), 10);
      if (!ts) return false;
      return (Date.now() - ts) < days * 86400000;
    } catch (_) { return false; }
  }
  function stamp(el) { try { window.localStorage.setItem(storeKey(el), String(Date.now())); } catch (_) { } }

  function initSection(sectionEl) {
    if (sectionEl._pwInit) return;
    sectionEl._pwInit = true;
    var ctl = wirePopup(sectionEl);
    if (!ctl) return;
    sectionEl._pwCtl = ctl; // so a theme-editor section:unload can release its scroll-lock

    var posted = !!sectionEl.querySelector("[data-pw-posted]");
    var opened = false;
    function openOnce() { if (opened) return; opened = true; stamp(sectionEl); ctl.open(); }

    // returning from a no-JS native submit: reopen straight to the success state
    if (posted) {
      if (ctl.form) ctl.form.markDone();
      openOnce();
      return;
    }

    // theme editor: always preview, ignore the frequency cap
    if (DESIGN) { ctl.open(); ctl.showLauncher(); return; }
    if (suppressed(sectionEl)) { ctl.showLauncher(); return; }

    var trigger = sectionEl.getAttribute("data-trigger") || "delay";
    if (trigger === "button") { ctl.showLauncher(); return; }
    if (trigger === "page_load") { openOnce(); return; }

    if (trigger === "delay") {
      var secs = parseInt(sectionEl.getAttribute("data-delay"), 10); if (isNaN(secs)) secs = 4;
      setTimeout(openOnce, secs * 1000); ctl.showLauncher(); return;
    }
    if (trigger === "scroll") {
      var pct = parseInt(sectionEl.getAttribute("data-scroll"), 10) || 35;
      var onScroll = function () {
        var docH = document.documentElement.scrollHeight - window.innerHeight;
        var reached = docH <= 0 ? 100 : ((window.scrollY || window.pageYOffset) / docH) * 100;
        if (reached >= pct) { window.removeEventListener("scroll", onScroll); openOnce(); }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      ctl.showLauncher(); return;
    }
    if (trigger === "exit") {
      var onOut = function (e) {
        if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) { document.removeEventListener("mouseout", onOut); openOnce(); }
      };
      document.addEventListener("mouseout", onOut);
      ctl.showLauncher(); return;
    }
    ctl.showLauncher();
  }

  function boot() {
    var els = document.querySelectorAll("[data-modulo-pw]");
    for (var i = 0; i < els.length; i++) initSection(els[i]);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  // theme editor: re-init on add/edit, and release a torn-down section's scroll-lock
  if (DESIGN) {
    document.addEventListener("shopify:section:load", function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector("[data-modulo-pw]") : null;
      if (el) initSection(el);
    });
    document.addEventListener("shopify:section:unload", function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector("[data-modulo-pw]") : null;
      if (el && el._pwCtl && el._pwCtl.release) el._pwCtl.release();
    });
  }
})();
